/**
 * CapabilityGate — [REQ-AUG-30 ACCESS-1 1.0a, ruling Q13] the route-level half of capability gating.
 *
 * Band 0 gated the NAV on `caps.CAP_*`. That is honest — the server re-reads the grant from the
 * database on every request, so hiding a nav item was never the boundary — but it left a **typed URL**
 * reaching a screen whose every write would 403. This is the wrapper that closes it, and it is
 * deliberately the same three-line shape as `StudentShell`'s feature `Gated`: one predicate, one
 * placeholder, no logic of its own.
 *
 * ⚠️ **THIS IS NOT A SECURITY BOUNDARY AND MUST NEVER BE MISTAKEN FOR ONE.** `requireCapability`
 * re-reads the grant per request, so a revoke bites on the very next call with no re-login. This only
 * stops the product offering a door the server will not open.
 *
 * ⚠️ **THE LOADING BRANCH RENDERS NOTHING RATHER THAN THE DENIAL.** `useMyCapabilities` resolves
 * asynchronously, so a naive `caps?.X ? children : <Denied/>` flashes "you do not have access" at
 * every legitimate holder on every cold load — telling a librarian they have been locked out, once a
 * day, for a fifth of a second. Absence of an answer is not a denial.
 */
import type { JSX } from 'react';
import { Lock } from 'lucide-react';
import { useMyCapabilities, type CapabilityKey } from '../../hooks/useMyCapabilities';

/** What a caller without the grant sees on a direct URL. Mirrors `FeatureDisabledState`'s shape. */
function CapabilityDeniedState({ label }: { label: string }): JSX.Element {
  return (
    <div
      data-capability-denied=""
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '55vh', gap: 12, padding: 24, textAlign: 'center' }}
    >
      <div style={{ width: 56, height: 56, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2, #EEE)', color: 'var(--t3)' }}>
        <Lock size={26} />
      </div>
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 800, color: 'var(--t1)', margin: 0 }}>
        You don&apos;t have access to {label}
      </h2>
      <p style={{ fontSize: 13.5, color: 'var(--t3)', lineHeight: 1.6, maxWidth: 420, margin: 0 }}>
        Your Super Admin can give you access from Staff Access. It takes effect the next time you open
        this page — you don&apos;t need to sign in again.
      </p>
    </div>
  );
}

export function CapabilityGate(
  { capability, label, children }: { capability: CapabilityKey; label: string; children: JSX.Element },
): JSX.Element | null {
  const { data: caps, isLoading } = useMyCapabilities();
  // See the header: no answer yet is not a denial.
  if (isLoading) return null;
  return caps?.[capability] ? children : <CapabilityDeniedState label={label} />;
}

export default CapabilityGate;
